import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { resolveImageUrl } from "@/lib/images";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="container-page py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-primary">Post not found</h1>
        <Link to="/blog" className="btn-secondary mt-6 inline-flex">Back to blog</Link>
      </div>
    );
  }
  if (!post) return <div className="container-page py-16">Loading…</div>;

  return (
    <article className="container-page py-12" data-testid="blog-post-page">
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent" data-testid="blog-back">
        <ArrowLeft className="h-4 w-4" /> All posts
      </Link>
      <header className="mt-8 max-w-3xl">
        {post.listing ? (
          <Link to={`/listing/${post.listing.id}`} className="chip" data-testid="blog-listing-link">
            {post.listing.category} · {post.listing.practice_name}
          </Link>
        ) : post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="blog-tag-row">
            {post.tags.slice(0, 4).map((t) => <span key={t} className="chip">{t}</span>)}
          </div>
        )}
        <h1 className="mt-4 font-display text-5xl font-bold leading-[1.05] text-primary lg:text-6xl" data-testid="blog-post-title">{post.title}</h1>
        {post.author_byline && (
          <p className="mt-4 text-base font-semibold text-primary" data-testid="blog-author-byline">By {post.author_byline}</p>
        )}
        <p className="mt-2 text-sm text-muted-foreground">
          {new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </p>
      </header>
      {post.hero_url && (
        <div className="mt-10 overflow-hidden rounded-2xl">
          <img src={resolveImageUrl(post.hero_url)} alt={post.title} className="h-auto w-full object-cover" />
        </div>
      )}
      <div
        className="prose prose-lg mt-10 max-w-3xl text-foreground/90 [&_a]:text-primary [&_a:hover]:text-accent [&_h2]:font-display [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-primary [&_h3]:font-display [&_h3]:text-2xl [&_h3]:font-bold [&_img]:my-6 [&_img]:rounded-xl [&_p]:my-5 [&_p]:leading-relaxed"
        dangerouslySetInnerHTML={{ __html: post.body_html }}
        data-testid="blog-post-body"
      />
      {post.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2 border-t border-border pt-6">
          {post.tags.map((t) => (
            <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="rounded-full bg-primary/8 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/15">
              #{t}
            </Link>
          ))}
        </div>
      )}
    </article>
  );
}
